/**
 * Default layout for the Dashboard page.
 *
 * Replicates the hardcoded layout:
 * - 3 MetricCards (Cash Collected, Revenue, Close Rate)
 * - 4 StatCards (Show Rate, Calls Due, Calls Taken, Calls Closed)
 * - 4 StatCards (Avg Cash/Call, Avg Cash/Close, Avg Cash/Application, Avg Cash/Opt-in)
 * - 2 AdditionalStats (Total Cash Applications, Total Cash Opt-ins)
 * - 3 AdditionalStats (New Cash, MRR, Total Gross Revenue)
 * - 2 AdditionalStats (Total Applications, Total Opt-ins)
 * - 1 full-width pieChart (Country)
 * - 1 areaChart (Cash Trend) + 1 pieChart (Lead Source) side-by-side
 * - 1 full-width pieChart (Content)
 * - 1 full-width table (Recent Calls)
 */

import type { DashboardLayout } from "../types";

export const DASHBOARD_DEFAULT: DashboardLayout = {
  widgets: [
    // --- Row 1: Metric Cards (y:0) ---
    {
      id: "dash-metric-cash",
      type: "metric",
      title: "Cash Collected",
      config: {
        metricKey: "totalCashCollected",
        format: "currencyAbbreviated",
        icon: "dollar",
        color: "brand",
        sparklineKey: "cashTrend",
      },
    },
    {
      id: "dash-metric-revenue",
      type: "metric",
      title: "Revenue",
      config: {
        metricKey: "totalRevenue",
        format: "currencyAbbreviated",
        icon: "trending",
        color: "green",
        sparklineKey: "cashTrend",
      },
    },
    {
      id: "dash-metric-closerate",
      type: "metric",
      title: "Close Rate",
      config: {
        metricKey: "closeRate",
        format: "percent",
        icon: "target",
        color: "blue",
      },
    },

    // --- Row 2: Stat Cards (y:3) ---
    {
      id: "dash-stat-showrate",
      type: "stat",
      title: "Show Rate",
      config: { metricKey: "showRate", format: "percent" },
    },
    {
      id: "dash-stat-callsdue",
      type: "stat",
      title: "Calls Due",
      config: { metricKey: "callsDue", format: "number" },
    },
    {
      id: "dash-stat-callstaken",
      type: "stat",
      title: "Calls Taken",
      config: { metricKey: "callsTaken", format: "number" },
    },
    {
      id: "dash-stat-callsclosed",
      type: "stat",
      title: "Calls Closed",
      config: { metricKey: "callsClosed", format: "number" },
    },

    // --- Row 3: Average Stat Cards (y:5) ---
    {
      id: "dash-stat-avgcashcall",
      type: "stat",
      title: "Avg. Cash/Call",
      config: { metricKey: "avgCashPerCall", format: "currency" },
    },
    {
      id: "dash-stat-avgcashclose",
      type: "stat",
      title: "Avg. Cash/Close",
      config: { metricKey: "avgCashPerClose", format: "currency" },
    },
    {
      id: "dash-stat-avgcashapplication",
      type: "stat",
      title: "Avg. Cash/Application",
      config: { metricKey: "avgCashPerApplication", format: "currency" },
    },
    {
      id: "dash-stat-avgcashoptin",
      type: "stat",
      title: "Avg. Cash/Opt-in",
      config: { metricKey: "avgCashPerOptin", format: "currency" },
    },

    // --- Row 4a: Cash per Lead Type (y:7) ---
    {
      id: "dash-addstat-appcash",
      type: "additionalStat",
      title: "Total Cash (Applications)",
      config: {
        metricKey: "totalApplicationsCash",
        format: "currency",
        description: "Cash collected from application leads",
      },
    },
    {
      id: "dash-addstat-optincash",
      type: "additionalStat",
      title: "Total Cash (Opt-ins)",
      config: {
        metricKey: "totalOptinsCash",
        format: "currency",
        description: "Cash collected from opt-in leads",
      },
    },

    // --- Row 4b: 6-card compact stats row (y:10) ---
    {
      id: "dash-addstat-newcash",
      type: "additionalStat",
      title: "New Cash Collected",
      config: {
        metricKey: "newCashCollected",
        format: "currencyAbbreviated",
        description: "Total cash from new deals (excludes MRR)",
      },
    },
    {
      id: "dash-addstat-mrr",
      type: "additionalStat",
      title: "MRR",
      config: {
        metricKey: "mrrCash",
        format: "currencyAbbreviated",
        description: "Monthly recurring revenue",
      },
    },
    {
      id: "dash-addstat-grossrev",
      type: "additionalStat",
      title: "Total Gross Revenue",
      config: {
        metricKey: "totalGrossRevenue",
        format: "currencyAbbreviated",
        description: "Total contract value (all revenue)",
      },
    },
    {
      id: "dash-addstat-totalapps",
      type: "additionalStat",
      title: "Total Applications",
      config: {
        metricKey: "totalApplications",
        format: "number",
        description: "Total application leads",
      },
    },
    {
      id: "dash-addstat-totaloptins",
      type: "additionalStat",
      title: "Total Opt-ins",
      config: {
        metricKey: "totalOptins",
        format: "number",
        description: "Total opt-in leads",
      },
    },
    {
      id: "dash-addstat-cancelrate",
      type: "additionalStat",
      title: "Cancel Rate",
      config: {
        metricKey: "cancelRate",
        format: "percent",
        description: "Cancelled calls / calls due",
      },
    },

    // --- Row 5: Full-width Country Pie Chart (y:16) ---
    {
      id: "dash-pie-country",
      type: "pieChart",
      title: "Closes by Country",
      config: { dataKey: "countryData", title: "Closes by Country" },
    },

    // --- Row 6: Cash Trend + Lead Source side-by-side (y:30) ---
    {
      id: "dash-area-cashtrend",
      type: "areaChart",
      title: "Cash Collected Trend",
      config: {
        dataKey: "cashTrend",
        title: "Cash Collected Trend",
        yFormat: "currency",
      },
    },
    {
      id: "dash-pie-leadsource",
      type: "pieChart",
      title: "Closes By Lead Source",
      config: { dataKey: "leadSourceData", title: "Closes By Lead Source" },
    },

    // --- Row 7: Full-width Content Pie Chart (y:42) ---
    {
      id: "dash-pie-content",
      type: "pieChart",
      title: "Closes by Content",
      config: { dataKey: "contentData", title: "Closes by Content" },
    },

    // --- Row 8: Full-width Recent Calls Table (y:58) ---
    {
      id: "dash-table-recentcalls",
      type: "table",
      title: "Recent Calls",
      config: { dataKey: "recentCalls", title: "Recent Calls" },
    },
  ],

  gridLayout: [
    // Row 1: 3 metric cards
    { i: "dash-metric-cash", x: 0, y: 0, w: 4, h: 3 },
    { i: "dash-metric-revenue", x: 4, y: 0, w: 4, h: 3 },
    { i: "dash-metric-closerate", x: 8, y: 0, w: 4, h: 3 },

    // Row 2: 4 stat cards
    { i: "dash-stat-showrate", x: 0, y: 3, w: 3, h: 2 },
    { i: "dash-stat-callsdue", x: 3, y: 3, w: 3, h: 2 },
    { i: "dash-stat-callstaken", x: 6, y: 3, w: 3, h: 2 },
    { i: "dash-stat-callsclosed", x: 9, y: 3, w: 3, h: 2 },

    // Row 3: 4 avg stat cards
    { i: "dash-stat-avgcashcall", x: 0, y: 5, w: 3, h: 2 },
    { i: "dash-stat-avgcashclose", x: 3, y: 5, w: 3, h: 2 },
    { i: "dash-stat-avgcashapplication", x: 6, y: 5, w: 3, h: 2 },
    { i: "dash-stat-avgcashoptin", x: 9, y: 5, w: 3, h: 2 },

    // Row 4a: cash per lead type (2 cards)
    { i: "dash-addstat-appcash", x: 0, y: 7, w: 6, h: 3 },
    { i: "dash-addstat-optincash", x: 6, y: 7, w: 6, h: 3 },

    // Row 4b: 6-card compact stats row (all in one line)
    { i: "dash-addstat-newcash", x: 0, y: 10, w: 2, h: 3 },
    { i: "dash-addstat-mrr", x: 2, y: 10, w: 2, h: 3 },
    { i: "dash-addstat-grossrev", x: 4, y: 10, w: 2, h: 3 },
    { i: "dash-addstat-totalapps", x: 6, y: 10, w: 2, h: 3 },
    { i: "dash-addstat-totaloptins", x: 8, y: 10, w: 2, h: 3 },
    { i: "dash-addstat-cancelrate", x: 10, y: 10, w: 2, h: 3 },

    // Row 5: full-width pie chart (Country)
    { i: "dash-pie-country", x: 0, y: 13, w: 12, h: 14 },

    // Row 6: area chart + pie chart side-by-side
    { i: "dash-area-cashtrend", x: 0, y: 27, w: 6, h: 12 },
    { i: "dash-pie-leadsource", x: 6, y: 27, w: 6, h: 12 },

    // Row 7: full-width pie chart (Content)
    { i: "dash-pie-content", x: 0, y: 39, w: 12, h: 16 },

    // Row 8: full-width table (Recent Calls)
    { i: "dash-table-recentcalls", x: 0, y: 55, w: 12, h: 16 },
  ],
};
